import type { PublicTopicBrief } from "./types";

export interface EstimatedBriefProgress {
  percent: number;
  label: string;
  elapsedSeconds: number;
}

const GENERATING_POINTS = [
  [0, 35],
  [10, 50],
  [25, 65],
  [40, 78],
  [55, 88],
  [75, 95],
  [120, 96],
] as const;

function interpolate(points: typeof GENERATING_POINTS, elapsedSeconds: number): number {
  for (let index = 1; index < points.length; index++) {
    const [rightSecond, rightPercent] = points[index];
    if (elapsedSeconds > rightSecond) continue;
    const [leftSecond, leftPercent] = points[index - 1];
    const ratio = (elapsedSeconds - leftSecond) / (rightSecond - leftSecond);
    return Math.round(leftPercent + (rightPercent - leftPercent) * ratio);
  }
  return points[points.length - 1][1];
}

/**
 * 根据近 7 天真实耗时拟合：
 * 检索通常不足 2 秒，模型整理约 24–91 秒、均值约 54 秒。
 * 进度只在阶段内平滑前进并封顶 96%，最终结果落库后才显示 100%。
 */
export function estimateBriefProgress(
  brief: PublicTopicBrief,
  nowMs = Date.now(),
): EstimatedBriefProgress {
  const elapsedSeconds = Math.max(
    0,
    Math.floor((nowMs - new Date(brief.updatedAt).getTime()) / 1_000),
  );
  if (brief.progress.stage === "completed") {
    return { percent: 100, label: "事件解读已完成", elapsedSeconds };
  }
  if (brief.progress.stage === "queued") {
    return {
      percent: Math.min(20, 10 + Math.floor(elapsedSeconds / 3)),
      label: "任务已进入队列",
      elapsedSeconds,
    };
  }
  if (brief.progress.stage === "searching") {
    return {
      percent: Math.min(34, 25 + elapsedSeconds * 3),
      label: "正在检索相关资料",
      elapsedSeconds,
    };
  }
  return {
    percent: interpolate(GENERATING_POINTS, elapsedSeconds),
    label: elapsedSeconds < 15
      ? "正在阅读和筛选资料"
      : elapsedSeconds < 40
        ? "正在组织事件脉络"
        : "正在核对表述并完成解读",
    elapsedSeconds,
  };
}
